<template>
    <div class="row mt-4">
        <div class="col-sm-6 mr-3">
            <!-- RESERVATION FORM -->
            <ValidationObserver v-slot="{ handleSubmit, invalid }" ref="reservationForm">
                <form role="form" @submit.prevent="handleSubmit(submitReservationForm)">
                    <h4 class="mb-3">{{ $t('general_info') }}</h4>
                    <!-- CLIENT -->
                    <div class="form-group">
                        <ValidationProvider name="clientName" rules="required" v-slot="{ errors }">
                            <label for="clientNameInput">{{ $t('client') }}</label>
                            <input class="form-control" id="clientNameInput" type="text"
                                   autocomplete="off" v-model="reservation.client" />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <hr />
                    <!-- MEDICATIONS IN RESERVATION -->
                    <h4 class="mb-3">{{ $t('medications') }}</h4>
                    <div class="card mt-3" v-for="med in reservation.medications" v-bind:key="med.vnr">
                        <!-- MEDICATION CARD HEADER -->
                        <div class="card-header">
                            <div class="d-flex row justify-content-between">
                                <div class="d-flex ml-2 align-self-center" :style="{ width: '85%' }">
                                    {{ getMedDisplayName(med, true) }}
                                </div>
                                <div class="d-flex mr-2 mt-0">
                                    <button
                                        class="btn btn-danger"
                                        size="sm"
                                        @click="deleteMedication(med.vnr)"
                                        :style="{
                                            display: 'inline-block',
                                            height: 'fit-content',
                                            padding: '0px 10px 2px 10px'
                                        }"
                                    >
                                        x
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- MEDICATION CARD BODY -->
                        <div class="card-body">
                            <!-- closed card -->
                            <div class="d-flex row ml-0" v-if="!getShowAmountEditor(med.vnr)">
                                <div class="d-flex align-self-center">
                                    {{ getMedDisplayAmount(med.amount, med.pills_amount, false, 'long') }}
                                </div>
                                <div class="d-flex ml-3">
                                    <input
                                        class="btn btn-sm btn-outline-primary"
                                        type="button"
                                        v-on:click="setShowAmountEditor(med.vnr, !getShowAmountEditor(med.vnr))"
                                        :value="$t('edit')"
                                    />
                                </div>
                            </div>
                            <!-- opened card -->
                            <div v-else>
                                <div class="d-flex flex-row justify-content-between">
                                    <!-- AMOUNT: FULL PACKAGES -->
                                    <div class="d-flex flex-column" :style="{ width: '28%' }">
                                        <ValidationProvider
                                            :name="`amount_${med.vnr}`"
                                            v-slot="{ errors }"
                                            :rules="{
                                                integer: true,
                                                not_negative: true,
                                                reservation_amount_required: {
                                                    vnrNameMatch: true,
                                                    thisField: med.amount,
                                                    otherField: med.pills_amount
                                                }
                                            }"
                                        >
                                            <label :for="`amountInput_${med.vnr}`">
                                                {{ $t('packages_long_plural_partitive') }}
                                            </label>
                                            <input
                                                type="text"
                                                autocomplete="off"
                                                :id="`amountInput_${med.vnr}`"
                                                class="form-control"
                                                v-model="med.amount"
                                                aria-describedby="basic-addon"
                                                v-on:keyup="if (med.amount === '') med.only_full_packages = true;"
                                            />
                                            <div class="invalid-feedback" style="display: block">
                                                <span>
                                                    {{ $t(errors[0]) }}
                                                </span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                    <!-- AMOUNT: INDIVIDUAL PIECES -->
                                    <div class="d-flex flex-column" :style="{ width: '68%' }">
                                        <ValidationProvider
                                            :name="`amountPartial_${med.vnr}`"
                                            v-slot="{ errors }"
                                            :rules="{
                                                integer: true,
                                                not_negative: true,
                                                reservation_max_value: {
                                                    vnrNameMatch: true,
                                                    maxValue: med.package_size - 1
                                                },
                                                reservation_amount_required: {
                                                    vnrNameMatch: true,
                                                    thisField: med.pills_amount,
                                                    otherField: med.amount
                                                }
                                            }"
                                        >
                                            <label
                                                :for="`amountPartialInput_${med.vnr}`"
                                                v-b-tooltip.hover
                                                :title="
                                                    med.package_size === 1 ? $t('reservation_cannot_set_pills') : ''
                                                "
                                            >
                                                {{ $t('pieces_long_plural_partitive') }}
                                            </label>
                                            <div class="input-group">
                                                <input
                                                    type="text"
                                                    autocomplete="off"
                                                    :id="`amountPartialInput_${med.vnr}`"
                                                    class="form-control"
                                                    v-model="med.pills_amount"
                                                    :aria-describedby="`amountPartialUnits_${med.vnr}`"
                                                    :disabled="med.package_size === 1"
                                                />
                                                <div class="input-group-append">
                                                    <span
                                                        class="input-group-text"
                                                        :id="`amountPartialUnits_${med.vnr}`"
                                                        :style="{ flex: 1, whiteSpace: 'normal' }"
                                                    >
                                                        {{ med.form }} {{ med.dosage_form }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="invalid-feedback" style="display: block">
                                                <span>
                                                    {{ $t(errors[0]) }}
                                                    {{
                                                        errors[0] === 'error_value_too_high' ? med.package_size - 1 : ''
                                                    }}
                                                </span>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <!-- "ONLY FULL PACKAGES" CHECKBOX -->
                                <div class="d-flex flex-row form-check mt-2 mb-3">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        v-model="med.only_full_packages"
                                        :id="`onlyFullPackages_${med.vnr}`"
                                    />
                                    <label class="form-check-label" :for="`onlyFullPackages_${med.vnr}`">
                                        {{ $t('full_packages_only') }}
                                    </label>
                                </div>
                                <!-- DOCTOR -->
                                <div class="form-group">
                                    <ValidationProvider
                                        :name="`doctorName_${med.vnr}`"
                                        rules="required"
                                        v-slot="{ errors }"
                                    >
                                        <label for="doctorNameInput">{{ $t('doctor') }}</label>
                                        <input
                                            class="form-control"
                                            :id="`doctorNameInput_${med.vnr}`"
                                            type="text"
                                            autocomplete="off"
                                            v-model="med.doctor"
                                        />
                                        <div class="invalid-feedback" style="display: block">
                                            <span>{{ $t(errors[0]) }}</span>
                                        </div>
                                    </ValidationProvider>
                                </div>
                                <!-- PRESCRIPTION -->
                                <div class="form-group">
                                    <ValidationProvider
                                        :name="`prescription_${med.vnr}`"
                                        :rules="{
                                            required: true
                                        }"
                                        v-slot="{ errors }"
                                    >
                                        <label for="prescriptionInput">{{ $t('prescription') }}</label>
                                        <input
                                            type="text"
                                            autocomplete="off"
                                            :id="`prescriptionInput_${med.vnr}`"
                                            class="form-control"
                                            v-model="med.prescription"
                                        />
                                        <div class="invalid-feedback" style="display: block">
                                            <span v-if="errors[0]">{{ $t(errors[0]) }}</span>
                                            <span
                                                v-else-if="checkDuplicatePrescription(med.prescription, 1)"
                                                :style="{ color: 'blue' }"
                                            >
                                                {{ $t('prescription_number_already_in_use') }}
                                            </span>
                                        </div>
                                    </ValidationProvider>
                                </div>
                                <!-- BUTTON: 'CLOSE' -->
                                <div class="d-flex flex-row mt-3">
                                    <input
                                        class="btn btn-sm btn-outline-primary"
                                        type="button"
                                        v-on:click="setShowAmountEditor(med.vnr, !getShowAmountEditor(med.vnr))"
                                        :value="$t('close')"
                                        :disabled="resMedHasErrors(med.vnr)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- BUTTON: 'ADD MEDICATION' -->
                    <div v-if="!showAddMedication" class="d-flex form-group mt-3 mb-3">
                        <input
                            type="button"
                            class="btn btn-md btn-primary"
                            v-on:click="setShowAddMedication"
                            :value="$t('add_medication')"
                            :disabled="submitClicked"
                        />
                    </div>
                    <hr />
                    <!-- BUTTONS: SUBMIT, CANCEL -->
                    <div class="d-flex flex-row mt-3">
                        <div class="d-flex flex-column mr-3">
                            <input
                                type="submit"
                                class="btn btn-md btn-success"
                                v-bind:disabled="
                                    submitClicked ||
                                        (reservation.medications.length < 1 && !addMedFormFilledAndValid) ||
                                        invalid
                                "
                                :value="$t(submitButtonName)"
                            />
                        </div>
                        <div class="d-flex flex-column">
                            <button
                                class="btn btn-md btn-secondary"
                                type="button"
                                v-on:click="$router.back()"
                                :disabled="submitClicked"
                            >
                                {{ $t('cancel') }}
                            </button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <!-- ADD MEDICATION FORM -->
        <div class="col-sm-5" v-if="showAddMedication">
            <h4 class="mb-3">{{ $t('add_medication') }}</h4>
            <ValidationObserver v-slot="{ handleSubmit, invalid }" ref="addMedicationForm">
                <form role="form" @submit.prevent="handleSubmit(submitAddMedicationForm)">
                    <div class="form-group">
                        <input
                            type="button"
                            class="btn btn-md btn-primary ml-sm"
                            v-on:click="() => searchMedGlobal()"
                            :value="$t('global_search')"
                        />
                    </div>
                    <!-- VNR -->
                    <div class="form-group">
                        <ValidationProvider
                            name="VNR"
                            :rules="{
                                min: vnrLength,
                                max: vnrLengthCustom,
                                unique_vnr: {
                                    resVnrs: reservation.medications.map(med => med.vnr)
                                },
                                vnr_valid: { vnrNameMatch: medication.vnrNameMatch }
                            }"
                            v-slot="{ errors }"
                        >
                            <label for="vnr">{{ $t('vnr') }}</label>
                            <input
                                autocomplete="off"
                                class="form-control"
                                id="vnr"
                                type="text"
                                v-model="medication.data.vnr"
                                v-on:keydown="clearVnrOptions"
                                v-on:keyup="getMedicationName"
                                :placeholder="searchingVnrs ? $t('searching') : ''"
                            />
                            <ul v-if="vnrOptions.length > 0" class="custom_ul">
                                <li v-for="item in vnrOptions" v-bind:key="item.vnr" v-on:click="chooseVnr(item.vnr)">
                                    <span>{{ getMedDisplayName(item, true, true) }}</span>
                                </li>
                            </ul>
                            <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                    {{ errors[0] === 'error_too_short' ? vnrLength : null }}
                                    {{ errors[0] === 'error_too_long' ? vnrLengthCustom : null }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- MEDICATION NAME -->
                    <div class="form-group">
                        <ValidationProvider
                            name="medName"
                            :rules="{
                                med_name_valid: { searchDone, vnrOptions }
                            }"
                            v-slot="{ errors }"
                        >
                            <label for="medication_name">{{ $t('name') }}</label>
                            <textarea
                                type="text"
                                id="medication_name"
                                class="form-control"
                                v-model="medication.showName"
                                v-on:keyup="getMedicationVnrsDelay"
                            />
                            <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- MEDICATION AVAILABLE AMOUNT -->
                    <div
                        class="form-group"
                        :style="{ color: 'grey', fontSize: '14px', fontStyle: 'italic' }"
                        v-if="
                            medication.vnrNameMatch &&
                                (medication.availablePackages !== null || medication.availablePills !== null)
                        "
                    >
                        {{
                            `${$t('available')}: ${getMedDisplayAmount(
                                medication.availablePackages,
                                medication.availablePills,
                                false,
                                'long'
                            )}`
                        }}
                    </div>
                    <!-- AMOUNT: FULL PACKAGES -->
                    <div class="form-group mb-2">
                        <ValidationProvider
                            name="Amount"
                            v-slot="{ errors }"
                            :rules="{
                                integer: true,
                                not_negative: true,
                                reservation_amount_required: {
                                    vnrNameMatch: medication.vnrNameMatch,
                                    thisField: medication.data.amount,
                                    otherField: medication.data.pills_amount
                                }
                            }"
                        >
                            <label for="amount">
                                {{ capitalizeFirstLetter($t('packages_long_plural_partitive')) }}
                            </label>
                            <div class="input-group">
                                <input
                                    type="text"
                                    autocomplete="off"
                                    id="amount"
                                    class="form-control"
                                    v-model="medication.data.amount"
                                    aria-describedby="basic-addon"
                                    v-on:keyup="
                                        if (medication.data.amount === '') medication.data.only_full_packages = true;
                                    "
                                />
                            </div>
                            <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- 'DON'T SPLIT FULL PACKAGES' CHECKBOX -->
                    <div class="form-group">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="medication.data.only_full_packages"
                                id="flexCheckDefault"
                                :disabled="!medication.vnrNameMatch || !(medication.data.amount > 0)"
                            />
                            <label class="form-check-label" for="flexCheckDefault">
                                {{ $t('full_packages_only') }}
                            </label>
                        </div>
                    </div>
                    <!-- AMOUNT: INDIVIDUAL PIECES -->
                    <div class="form-group" v-if="showPills">
                        <ValidationProvider
                            name="PartialAmount"
                            v-slot="{ errors }"
                            :rules="{
                                integer: true,
                                not_negative: true,
                                reservation_max_value: {
                                    vnrNameMatch: medication.vnrNameMatch,
                                    maxValue: medication.data.package_size - 1
                                },
                                reservation_amount_required: {
                                    vnrNameMatch: medication.vnrNameMatch,
                                    thisField: medication.data.pills_amount,
                                    otherField: medication.data.amount
                                }
                            }"
                        >
                            <label for="amount_partial">
                                {{ capitalizeFirstLetter($t('pieces_long_plural_partitive')) }}
                            </label>
                            <div class="input-group">
                                <input
                                    type="text"
                                    autocomplete="off"
                                    id="amount_partial"
                                    class="form-control"
                                    v-model="medication.data.pills_amount"
                                    aria-describedby="pills_amount"
                                />
                                <div class="input-group-append">
                                    <span class="input-group-text" id="pills_amount">
                                        {{ medication.data.form }}
                                        {{ medication.data.dosage_form }}
                                    </span>
                                </div>
                            </div>
                            <div class="invalid-feedback" style="display: block">
                                <span>
                                    {{ $t(errors[0]) }}
                                    {{ errors[0] === 'error_value_too_high' ? medication.data.package_size - 1 : '' }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- 'SHOW PARTIAL' BUTTON -->
                    <div
                        :style="{ display: 'inline' }"
                        v-b-tooltip.hover
                        :title="medication.data.package_size === 1 ? $t('reservation_cannot_set_pills') : ''"
                    >
                        <button
                            class="btn btn-md btn-light"
                            v-on:click="setPillsField"
                            type="button"
                            :disabled="medication.data.package_size === 1"
                        >
                            {{ showPills ? $t('hide_partial_packages') : $t('show_partial_packages') }}
                        </button>
                    </div>
                    <!-- DOCTOR -->
                    <div class="form-group mt-4">
                        <ValidationProvider name="doctorName" rules="required" v-slot="{ errors }">
                            <label for="doctorNameInput">{{ $t('doctor') }}</label>
                            <input
                                class="form-control"
                                id="doctorNameInput"
                                type="text"
                                autocomplete="off"
                                v-model="medication.data.doctor"
                            />
                            <div class="invalid-feedback" style="display: block">
                                <span>{{ $t(errors[0]) }}</span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- PRESCRIPTION -->
                    <div class="form-group">
                        <ValidationProvider
                            name="prescription"
                            :rules="{
                                required: true
                            }"
                            v-slot="{ errors }"
                        >
                            <label for="prescriptionInput">{{ $t('prescription') }}</label>
                            <input
                                type="text"
                                autocomplete="off"
                                id="prescriptionInput"
                                class="form-control"
                                v-model="medication.data.prescription"
                            />
                            <div class="invalid-feedback" style="display: block">
                                <span v-if="errors[0]">{{ $t(errors[0]) }}</span>
                                <span
                                    v-else-if="checkDuplicatePrescription(medication.data.prescription, 0)"
                                    :style="{ color: 'blue' }"
                                >
                                    {{ $t('prescription_number_already_in_use') }}
                                </span>
                            </div>
                        </ValidationProvider>
                    </div>
                    <!-- 'SUBMIT' & 'CANCEL' BUTTONS -->
                    <div class="d-flex flex-row mt-4">
                        <div class="d-flex flex-column mr-3">
                            <input
                                type="submit"
                                class="btn btn-md btn-primary"
                                :disabled="!medication.vnrNameMatch || invalid"
                                :value="$t('add_to_reservation')"
                            />
                        </div>
                        <div class="d-flex flex-column">
                            <input
                                type="button"
                                class="btn btn-md btn-secondary"
                                v-on:click="setShowAddMedication"
                                :value="$t('cancel')"
                            />
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </div>
        <!-- "ADD MEDICATION BEFORE SUBMIT? MODAL" -->
        <b-modal id="submitConfirmationModal" centered :title="$t('include_med_in_res_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ modalText }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="() => submitReservationFormModal(true)">
                        {{ $t('yes') }}
                    </button>
                    <button class="btn btn-secondary" @click="() => submitReservationFormModal(false)">
                        {{ $t('no') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { getFilteredMedications,
    getMedicationAvailableAmount,
    handleAxiosError,
    getMedicationInventory } from '@/services/axiosService';
import { getMedDisplayAmount, getMedDisplayName } from '@/services/medicationService';
import { capitalizeFirstLetter } from '@/services/textService';
import GlobalSearch from './GlobalSearch';

export default {
    props: {
        errors: {
            type: Object,
            required: false
        },
        reservation: {
            type: Object,
            required: true
        },
        showAddMedicationInitially: {
            type: Boolean,
            required: false
        },
        showAmountEditorInitially: {
            type: Boolean,
            required: false
        },
        submitButtonName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            capitalizeFirstLetter,
            getMedDisplayAmount,
            getMedDisplayName,
            medication: {
                data: {
                    doctor: '',
                    prescription: '',
                    vnr: ''
                },
                showName: '',
                showNamePrevious: ''
            },
            searchDone: false,
            searchingVnrs: false,
            searchItem: '',
            searchVnrDelay: 500,
            searchVnrTimer: null,
            showAddMedication: this.showAddMedicationInitially || false,
            showAmountEditor: [],
            showPills: false,
            submitClicked: false,
            vnrLength: 6,
            vnrLengthCustom: 7,
            vnrOptions: []
        };
    },
    created() {
        // clear "add medication" form fields and set amount editor visibility based on input parameter
        this.clearMedication();
        this.setShowAmountEditor(null, this.showAmountEditorInitially || false);
    },
    computed: {
        addMedFormFilledAndValid() {
            if (this.medication.vnrNameMatch && this.$refs.addMedicationForm.flags.valid) {
                return true;
            }
            return false;
        },
        modalText() {
            return this.$t('include_med_in_res_text').replace('MEDNAME', `"${this.medication.showName}"`);
        }
    },
    methods: {
        // check if prescription number is already in use in reservation
        checkDuplicatePrescription(currentPrescription, allowed = 0) {
            const matches = this.reservation.medications.filter(
                med => med.prescription.toLowerCase().trim() === currentPrescription.toLowerCase().trim()
            );
            return matches.length > allowed;
        },
        // choose vnr from selection dropdown
        async chooseVnr(vnr) {
            const medication = this.vnrOptions.find(medication => medication.vnr === vnr);
            this.vnrOptions = [];
            await this.processMedication(medication);
            this.medication.vnrNameMatch = true;
            this.searchDone = false;
        },
        // clear "add medication" fields and variables
        clearMedication() {
            this.medication = {
                data: {
                    amount: null,
                    company: '',
                    doctor: '',
                    dosage: '',
                    dosage_form: '',
                    form: '',
                    name: '',
                    package_size: '',
                    pills_amount: null,
                    prescription: '',
                    supplier: '',
                    only_full_packages: true,
                    vnr: this.medication.data.vnr
                },
                availablePackages: null,
                availablePills: null,
                showAmountEditor: [],
                showName: '',
                showNamePrevious: '',
                vnrNameMatch: null
            };
        },
        // clear vnr selection options
        clearVnrOptions() {
            this.vnrOptions = [];
        },
        // delete medication from reservation
        deleteMedication(vnr) {
            this.reservation.medications = this.reservation.medications.filter(med => med.vnr !== vnr);
        },
        // get medication's available amount
        async getMedAvailableAmount() {
            try {
                const availableAmount = await getMedicationAvailableAmount(this.medication.data.vnr);
                this.medication.availablePackages = Math.max(
                    0,
                    Math.floor(availableAmount / this.medication.data.package_size)
                );
                this.medication.availablePills = Math.max(0, availableAmount % this.medication.data.package_size);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // get medication info based on vnr
        async getMedicationName() {
            this.searchDone = false;
            this.clearMedication();
            // proceed only if entered vnr has supported length
            if (
                this.medication.data.vnr.length === this.vnrLength ||
                this.medication.data.vnr.length === this.vnrLengthCustom
            ) {
                try {
                    const params = { searchVnr: this.medication.data.vnr, searchVnrExact: true };
                    const result = await getFilteredMedications({ params });
                    // process result
                    if (result.length) {
                        const medication = result[0];
                        console.log(medication)
                        await this.processMedication(medication);
                        this.medication.vnrNameMatch = true;
                        this.showPills = false;
                    } else {
                        // try to get from inventory(if exists),
                        // it means it is removed from DB but still in the local inventory
                        try {
                            const result = await getMedicationInventory(this.medication.data.vnr);
                            if (!result) {
                                this.medication.vnrNameMatch = false;
                            } else {
                                const medFromInventory = result;
                                console.log(medFromInventory)

                                this.medication.data = {
                                    ...medFromInventory,
                                    amount: this.medication.data.amount,
                                    pills_amount: this.medication.data.pills_amount,
                                    only_full_packages: this.medication.data.only_full_packages
                                };
                                this.medication.showName = this.getMedDisplayName(this.medication.data, false);
                                this.medication.showNamePrevious = this.medication.showName;
                                await this.getMedAvailableAmount();

                                this.medication.vnrNameMatch = true;
                                this.showPills = false;
                            }
                        } catch (e) {
                            this.medication.vnrNameMatch = false;
                        }
                    }
                } catch (error) {
                    handleAxiosError(error);
                }
            }
        },
        // get possible medication vnrs based on med name input
        async getMedicationVnrs() {
            // if name not changed, do nothing
            if (this.medication.showName.trim() === this.medication.showNamePrevious) {
                return;
            }
            // clear previous vnr selection
            if (this.medication.vnrNameMatch) {
                this.medication.vnrNameMatch = null;
                this.medication.data.vnr = '';
            }
            // only proceed if at least three characters entered
            if (this.medication.showName.trim().length >= 3) {
                try {
                    this.searchingVnrs = true;
                    const params = { searchName: this.medication.showName.trim(), searchNameExact: false };
                    this.vnrOptions = await getFilteredMedications({ params });
                    this.searchDone = true;
                } catch (error) {
                    handleAxiosError(error);
                }
            } else if (this.medication.showName.trim().length === 0) {
                // if name field cleared, clear any existing vnr options
                this.searchDone = false;
                this.vnrOptions = [];
            }
            this.searchingVnrs = false;
        },
        // include delay in medication vnr search to prevent unnecessary queries
        getMedicationVnrsDelay() {
            clearTimeout(this.searchVnrTimer);
            this.searchVnrTimer = setTimeout(() => this.getMedicationVnrs(), this.searchVnrDelay);
        },
        // get amound editor visibility value for vnr
        getShowAmountEditor(vnr) {
            return this.showAmountEditor[`vnr_${vnr}`];
        },
        // process medication info fields
        async processMedication(medication) {
            this.medication.data = {
                ...medication,
                amount: this.medication.data.amount,
                doctor: this.medication.data.doctor,
                only_full_packages: this.medication.data.only_full_packages,
                pills_amount: this.medication.data.pills_amount,
                prescription: this.medication.data.prescription
            };
            delete this.medication.data.atc;
            delete this.medication.data.chemical;
            delete this.medication.data.company_reseller;
            delete this.medication.data.drug;
            delete this.medication.data.editable;
            delete this.medication.data.id;
            delete this.medication.data.webtool_action;
            this.medication.showName = this.getMedDisplayName(this.medication.data, false);
            this.medication.showNamePrevious = this.medication.showName;
            await this.getMedAvailableAmount();
        },
        // check if medication in reservation has errors
        resMedHasErrors(vnr) {
            if (!this.$refs.reservationForm) {
                return false;
            }

            const errors = this.$refs.reservationForm.errors;
            if (
                (errors[`amount_${vnr}`] && errors[`amount_${vnr}`].length) ||
                (errors[`amountPartial_${vnr}`] && errors[`amountPartial_${vnr}`].length) ||
                (errors[`doctorName_${vnr}`] && errors[`doctorName_${vnr}`].length) ||
                (errors[`prescription_${vnr}`] && errors[`prescription_${vnr}`].length)
            ) {
                return true;
            }
            return false;
        },
        // global medication search
        searchMedGlobal() {
            this.$modal.show(GlobalSearch, { update: this.setVNR }, { height: '400', width: '600', root: this.$root });
        },
        // set visibility of add medication form's individual-pieces (i.e. "pills") field
        setPillsField() {
            if (this.showPills) this.medication.data.pills_amount = null;
            this.showPills = !this.showPills;
        },
        // set visibility of add medication form
        setShowAddMedication() {
            if (this.showAddMedication) {
                this.medication.data.vnr = '';
                this.clearMedication();
            }
            this.showAddMedication = !this.showAddMedication;
        },
        // set visibility of reserved medication's amount editor;
        // if no vnr specified, set visibility for all reserved meds
        setShowAmountEditor(vnr, value) {
            if (!vnr) {
                this.reservation.medications.forEach(med => {
                    this.$set(this.showAmountEditor, `vnr_${med.vnr}`, value);
                });
            } else {
                this.$set(this.showAmountEditor, `vnr_${vnr}`, value);
            }
        },
        // set vnr of medication to be added and retrieve its info based on vnr
        setVNR(value) {
            this.medication.data.vnr = value.vnr;
            this.getMedicationName();
        },
        // submit "add medication" form (add medication to reservation)
        submitAddMedicationForm() {
            this.$refs.addMedicationForm.reset();
            this.medication.data.amount = this.medication.data.amount ? this.medication.data.amount : '0';
            this.medication.data.pills_amount = this.medication.data.pills_amount
                ? this.medication.data.pills_amount
                : '0';
            this.medication.data.doctor = this.medication.data.doctor.trim();
            this.medication.data.prescription = this.medication.data.prescription.trim();
            this.reservation.medications.push(_.clone(this.medication.data));
            this.reservation.medications = _.sortBy(this.reservation.medications, ['vnr']);
            this.setShowAmountEditor(this.medication.data.vnr, this.showAmountEditorInitially);
            this.setShowAddMedication(); // hide add medication form
        },
        // submit resercation form
        submitReservationForm() {
            this.submitClicked = true;
            // if add medication form contains valid data, show "add med to res?" modal
            if (this.showAddMedication) {
                if (this.addMedFormFilledAndValid) {
                    this.$bvModal.show('submitConfirmationModal');
                    return;
                }
                this.setShowAddMedication();
            }
            // trim reservation info field values
            this.reservation.client = this.reservation.client.trim();
            this.reservation.medications = this.reservation.medications.map(med => {
                return {
                    ...med,
                    doctor: med.doctor.trim(),
                    prescription: med.prescription.trim()
                };
            });
            // emit submit action
            this.$emit('submit');
        },
        // handle reservation submit via modal
        submitReservationFormModal(addMed) {
            this.$bvModal.hide('submitConfirmationModal');
            if (addMed) {
                // if user selected to add extra med, add it and then submit reservation form
                this.submitAddMedicationForm();
                this.submitReservationForm();
            } else if (this.reservation.medications.length) {
                // if extra med not added but res already contains med(s), hide add med form and submit res form
                this.setShowAddMedication();
                this.submitReservationForm();
            } else {
                // if res has no medications, do nothing
                this.submitClicked = false;
            }
        }
    },
    mounted() {
        // set errors returned from backend in relevant reservation form fields
        if (Object.keys(this.errors).length) {
            for (const key in this.errors) {
                this.$refs.reservationForm.setErrors({ [key]: [this.errors[key]] });
            }
        }
        // handle other "hide modal" actions (background click, esc key press, modal close click)
        this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
            if (modalId === 'submitConfirmationModal' && ['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
